/**
 * Per-user "My taste" recommendation weights.
 *
 * Mirrors the SWR-style pattern in `lib/favorites.tsx`:
 *   1. Hydrate from `localStorage` synchronously so the UI doesn't flicker
 *      between "default weights" and "your saved weights" on cold load.
 *   2. Kick off `GET /api/me/taste/settings` in the background. Server wins
 *      if it disagrees with the cache.
 *   3. Every mutation updates local state + cache *immediately* and
 *      schedules a debounced `PUT /api/me/taste/settings`. Drags on the
 *      sliders therefore feel instant but only one PUT lands per pause.
 *
 * Single source of truth: `weights` is the only persisted state. The
 * "active preset" badge and per-signal toggles are *derived* in the UI from
 * the current weights via `findMatchingPreset()` / `weights[key] > 0`.
 * Presets, sliders, and toggles all just call `setWeights(next)` \u2014 every
 * other control updates automatically.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { api, isAuthPending } from './api';
import { toast } from './dialogs';
import {
  DEFAULT_WEIGHTS,
  TASTE_PRESETS,
  WEIGHT_KEYS,
  findMatchingPreset,
  normalizeWeights,
  type PresetName,
  type WeightKey,
  type Weights
} from './tasteScore';

const STORAGE_KEY = 'mh.tasteWeights.v1';
const PUT_DEBOUNCE_MS = 500;

interface ServerSettings {
  weights: Weights | null;
  presetName: PresetName | string | null;
  updatedAt: string | null;
}

interface TasteSettingsContextValue {
  /** Current weights. Always a full 7-key object \u2014 falls back to defaults. */
  weights: Weights;
  /** True once the server fetch resolves (or fails). Cached values render
   *  before this is true so the UI doesn't flicker. */
  ready: boolean;
  /** True when there is an unsaved change waiting to be PUT to the server. */
  isSaving: boolean;
  /** True when the current weights match the factory `balanced` preset. */
  isDefault: boolean;
  /** Currently-matching preset name, or `null` for "Custom". */
  activePreset: PresetName | null;
  /** Replace the entire weights object (clamps + persists). */
  setWeights: (next: Weights) => void;
  /** Update a single signal weight in-place. */
  setWeight: (key: WeightKey, value: number) => void;
  /** Snap to one of the built-in presets. */
  applyPreset: (name: PresetName) => void;
  /** Toggle a signal off (\u2192 0) or on (\u2192 restore to the `balanced` value). */
  toggleSignal: (key: WeightKey) => void;
  /** Reset to the factory `balanced` preset. */
  resetToDefault: () => void;
}

const DEFAULT_CONTEXT: TasteSettingsContextValue = {
  weights: DEFAULT_WEIGHTS,
  ready: false,
  isSaving: false,
  isDefault: true,
  activePreset: 'balanced',
  setWeights: () => { /* no-op */ },
  setWeight: () => { /* no-op */ },
  applyPreset: () => { /* no-op */ },
  toggleSignal: () => { /* no-op */ },
  resetToDefault: () => { /* no-op */ }
};

const TasteSettingsContext = createContext<TasteSettingsContextValue | null>(null);

function readCachedWeights(): Weights | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return normalizeWeights(JSON.parse(raw));
  } catch {
    return null;
  }
}

function writeCachedWeights(weights: Weights): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(weights)); }
  catch { /* quota \u2014 non-fatal */ }
}

function clearCachedWeights(): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* */ }
}

function weightsEqualExact(a: Weights, b: Weights): boolean {
  for (const key of WEIGHT_KEYS) {
    if (a[key] !== b[key]) return false;
  }
  return true;
}

export function TasteSettingsProvider({ children }: { children: ReactNode }) {
  const isAuthed = useIsAuthenticated();
  const [weights, setWeightsState] = useState<Weights>(
    () => readCachedWeights() ?? DEFAULT_WEIGHTS
  );
  const [ready, setReady] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Debounce timer for the PUT call. Stored in a ref so unmount can flush.
  const putTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Latest weights we've scheduled a write for (avoids stale closures).
  const pendingWeights = useRef<Weights>(weights);
  // True after the first user-initiated mutation; we skip persisting
  // during the initial hydrate \u2192 fetch flow to avoid a no-op round trip.
  const userTouched = useRef(false);
  // Last error toast timestamp, to avoid spamming the UI on flaky networks.
  const lastErrorToast = useRef(0);

  const flushNow = useCallback(() => {
    if (!putTimer.current) return;
    clearTimeout(putTimer.current);
    putTimer.current = null;
    const presetName = findMatchingPreset(pendingWeights.current) ?? 'custom';
    setIsSaving(true);
    api.put<void>('/api/me/taste/settings', {
      weights: pendingWeights.current,
      presetName
    })
      .then(() => { setIsSaving(false); })
      .catch((err) => {
        setIsSaving(false);
        if (isAuthPending(err)) return;
        const now = Date.now();
        if (now - lastErrorToast.current > 10_000) {
          lastErrorToast.current = now;
          toast.error('Could not save taste settings. Changes are kept on this device.');
        }
        // eslint-disable-next-line no-console
        console.warn('Could not save taste settings:', err);
      });
  }, []);

  const persist = useCallback((next: Weights) => {
    pendingWeights.current = next;
    setIsSaving(true);
    if (putTimer.current) clearTimeout(putTimer.current);
    putTimer.current = setTimeout(flushNow, PUT_DEBOUNCE_MS);
  }, [flushNow]);

  // Hydrate from server. Local cache already populated initial state.
  useEffect(() => {
    if (!isAuthed) {
      setWeightsState(DEFAULT_WEIGHTS);
      setReady(false);
      userTouched.current = false;
      clearCachedWeights();
      if (putTimer.current) { clearTimeout(putTimer.current); putTimer.current = null; }
      return;
    }
    let cancelled = false;
    api.get<ServerSettings>('/api/me/taste/settings')
      .then((payload) => {
        if (cancelled) return;
        // Don't clobber an in-flight user edit with a slow server response.
        if (userTouched.current) {
          setReady(true);
          return;
        }
        if (payload?.weights) {
          const next = normalizeWeights(payload.weights);
          setWeightsState(next);
          writeCachedWeights(next);
        }
        setReady(true);
      })
      .catch((err) => {
        if (cancelled) return;
        if (!isAuthPending(err)) {
          // eslint-disable-next-line no-console
          console.warn('Could not load taste settings:', err);
        }
        setReady(true);
      });
    return () => { cancelled = true; };
  }, [isAuthed]);

  // Flush any pending PUT on tab close / hide. Browsers don't reliably run
  // async network requests during `unload` or React cleanup, so we use
  // `navigator.sendBeacon` (best-effort, fire-and-forget) on `pagehide`.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const flushBeacon = () => {
      if (!putTimer.current) return;
      clearTimeout(putTimer.current);
      putTimer.current = null;
      try {
        const presetName = findMatchingPreset(pendingWeights.current) ?? 'custom';
        const body = JSON.stringify({ weights: pendingWeights.current, presetName });
        if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
          const blob = new Blob([body], { type: 'application/json' });
          // NOTE: Easy Auth cookies are sent automatically with sendBeacon.
          navigator.sendBeacon('/api/me/taste/settings', blob);
        }
      } catch { /* best effort */ }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushBeacon();
    };
    window.addEventListener('pagehide', flushBeacon);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', flushBeacon);
      document.removeEventListener('visibilitychange', onVisibility);
      // Component unmount: try a synchronous beacon too.
      flushBeacon();
    };
  }, []);

  // Cross-tab sync: when another tab writes the cache, mirror it here.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY || !e.newValue) return;
      try {
        const next = normalizeWeights(JSON.parse(e.newValue));
        setWeightsState((prev) => (weightsEqualExact(prev, next) ? prev : next));
      } catch { /* ignore malformed payloads */ }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setWeights = useCallback((next: Weights) => {
    const normalized = normalizeWeights(next);
    setWeightsState((prev) => {
      if (weightsEqualExact(prev, normalized)) return prev;
      writeCachedWeights(normalized);
      userTouched.current = true;
      persist(normalized);
      return normalized;
    });
  }, [persist]);

  const setWeight = useCallback((key: WeightKey, value: number) => {
    setWeightsState((prev) => {
      const clamped = Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));
      if (prev[key] === clamped) return prev;
      const next: Weights = { ...prev, [key]: clamped };
      writeCachedWeights(next);
      userTouched.current = true;
      persist(next);
      return next;
    });
  }, [persist]);

  const applyPreset = useCallback((name: PresetName) => {
    const preset = TASTE_PRESETS[name];
    if (!preset) return;
    setWeights({ ...preset });
  }, [setWeights]);

  const toggleSignal = useCallback((key: WeightKey) => {
    setWeightsState((prev) => {
      const wasOn = prev[key] > 0;
      const nextValue = wasOn ? 0 : (TASTE_PRESETS.balanced[key] ?? 0.25);
      if (prev[key] === nextValue) return prev;
      const next: Weights = { ...prev, [key]: nextValue };
      writeCachedWeights(next);
      userTouched.current = true;
      persist(next);
      return next;
    });
  }, [persist]);

  const resetToDefault = useCallback(() => {
    setWeights({ ...DEFAULT_WEIGHTS });
  }, [setWeights]);

  const activePreset = useMemo(() => findMatchingPreset(weights), [weights]);
  const isDefault = activePreset === 'balanced';

  const value = useMemo<TasteSettingsContextValue>(() => ({
    weights,
    ready,
    isSaving,
    isDefault,
    activePreset,
    setWeights,
    setWeight,
    applyPreset,
    toggleSignal,
    resetToDefault
  }), [weights, ready, isSaving, isDefault, activePreset, setWeights, setWeight, applyPreset, toggleSignal, resetToDefault]);

  return <TasteSettingsContext.Provider value={value}>{children}</TasteSettingsContext.Provider>;
}

export function useTasteSettings(): TasteSettingsContextValue {
  const ctx = useContext(TasteSettingsContext);
  if (!ctx) return DEFAULT_CONTEXT;
  return ctx;
}
